import { useEffect, useState } from 'react';

/**
 * Rotating page index for a carousel. Same cadence/visibility rules as
 * `components/carousel/Carousel.jsx`, but exposes the index so the column can render page dots.
 */
export const useCarouselIndex = (pageCount: number, intervalMs: number): number => {
  const [current, setCurrent] = useState(0);

  // Keep the index valid when pages shrink (e.g. patients leave a phase).
  useEffect(() => {
    setCurrent(prev => (pageCount <= 0 ? 0 : prev % pageCount));
  }, [pageCount]);

  useEffect(() => {
    if (pageCount <= 1) return undefined;

    let timer: number | null = null;
    const ms = Math.max(500, intervalMs || 7000);

    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      stop();
      timer = window.setInterval(() => setCurrent(prev => (prev + 1) % pageCount), ms);
    };
    const onVisibility = () => (document.hidden ? stop() : start());

    document.addEventListener('visibilitychange', onVisibility);
    start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pageCount, intervalMs]);

  return pageCount <= 0 ? 0 : Math.min(current, pageCount - 1);
};
